import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Chip,
  Spinner,
  Divider,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  RadioGroup,
  Radio,
  addToast,
} from "@heroui/react";
import {
  CreditCard,
  Trash2,
  AlertTriangle,
  DollarSign,
  Receipt,
  Plus,
  Star,
  Calendar,
} from "lucide-react";
import dayjs from "dayjs";

import {
  getEnterpriseBillingInfoAPI,
  getPaymentCardListAPI,
  deletePaymentCardAPI,
  setPrimaryPaymentCardAPI,
  startEnterpriseBillingAPI,
  getEnterpriseBillingHistoryAPI,
} from "@/api/payment";
import type {
  PaymentCard,
  EnterpriseSubscriptionPlan,
  UserPaymentHistory,
} from "@/api/payment.type";
import { useUserStore } from "@/store/user";
import { useHttpRequest } from "@/hooks/useHttpRequest";
import useTossPayments from "@/hooks/useTossPayments";
import useConsumePaymentSearchParams from "@/hooks/useConsumePaymentSearchParams";

const searchSchema = z.object({
  page: z.number().min(1).catch(1),
  size: z.number().min(10).max(100).catch(10),
});

// KRW 포맷 헬퍼 함수
const formatKRW = (amount: number): string => {
  return new Intl.NumberFormat("ko-KR", {
    style: "currency",
    currency: "KRW",
  }).format(amount);
};

export const Route = createFileRoute("/(authenticated)/payment/")({
  validateSearch: searchSchema,
  loader: async () => {
    const user = useUserStore.getState().user;

    try {
      if (user) {
        const billingInfo = await getEnterpriseBillingInfoAPI(
          user?.enterpriseInfo?.enterpriseId!
        ).then((r) => r.data);

        return { billingInfo };
      }
      console.log("billingInfo: null");
      return { billingInfo: null };
    } catch (error) {
      return { billingInfo: null };
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { billingInfo } = Route.useLoaderData();
  const { page, size } = Route.useSearch();

  const navigate = Route.useNavigate();
  const user = useUserStore((state) => state.user);
  const enterpriseId = user?.enterpriseInfo?.enterpriseId;

  // 상태 관리
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<
    "MONTHLY" | "YEARLY"
  >("MONTHLY");
  const [isConfirmPaymentModalOpen, setIsConfirmPaymentModalOpen] =
    useState(false);
  const [isStartingPayment, setIsStartingPayment] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<PaymentCard | null>(null);
  const [isDeletingCard, setIsDeletingCard] = useState(false);

  // 토스 페이먼츠 훅
  const { addPaymentCardToToss } = useTossPayments();

  // 결제 이력 조회
  const {
    data: historyResponse,
    isLoading: isLoadingHistory,
    request: requestHistory,
  } = useHttpRequest(() =>
    getEnterpriseBillingHistoryAPI(enterpriseId!, { page, size }).then(
      (res) => res
    )
  );

  const histories = historyResponse?.data || [];
  const totalPages = historyResponse?.totalPage || 0;

  // 카드 리스트 조회
  const loadCards = async () => {
    if (!enterpriseId) return;
    setIsLoadingCards(true);
    try {
      const response = await getPaymentCardListAPI(enterpriseId);
      setCards(response.data || []);
    } catch (error) {
      addToast({
        title: "카드 목록을 불러오는데 실패했습니다.",
        description: "카드 목록을 불러오는데 실패했습니다.",
        color: "danger",
      });
    } finally {
      setIsLoadingCards(false);
    }
  };

  // 카드 등록 완료 콜백
  useConsumePaymentSearchParams({
    onAddCardSuccess: () => {
      loadCards();
    },
    onAddCardFail: () => {
      // hook 내부에서 이미 toast 처리
    },
  });

  // 카드 등록
  const handleAddCard = () => {
    addPaymentCardToToss();
  };

  // 대표 카드 설정
  const handleSetPrimaryCard = async (cardId: number) => {
    if (!enterpriseId) return;
    try {
      await setPrimaryPaymentCardAPI(enterpriseId, cardId);
      addToast({
        title: "대표 카드가 설정되었습니다.",
        color: "success",
      });
      loadCards();
    } catch (error) {
      addToast({
        title: "대표 카드 설정에 실패했습니다.",
        color: "danger",
      });
    }
  };

  // 카드 삭제
  const handleDeleteCard = async () => {
    if (!enterpriseId || !cardToDelete) return;
    setIsDeletingCard(true);
    try {
      await deletePaymentCardAPI(enterpriseId, cardToDelete.id);
      addToast({
        title: "카드가 삭제되었습니다.",
        color: "success",
      });
      setCardToDelete(null);
      loadCards();
    } catch (error) {
      addToast({
        title: "카드 삭제에 실패했습니다.",
        color: "danger",
      });
    } finally {
      setIsDeletingCard(false);
    }
  };

  // 결제 시작
  const handleStartPayment = async () => {
    if (!enterpriseId) return;
    setIsStartingPayment(true);
    try {
      await startEnterpriseBillingAPI(enterpriseId, selectedBillingCycle);
      addToast({
        title: "결제가 시작되었습니다.",
        color: "success",
      });
      setIsConfirmPaymentModalOpen(false);
      requestHistory();
    } catch (error) {
      addToast({
        title: "결제 시작에 실패했습니다.",
        color: "danger",
      });
    } finally {
      setIsStartingPayment(false);
    }
  };

  // 결제 상태 칩 색상
  const getPaymentStatusColor = (
    status: UserPaymentHistory["status"]
  ): "success" | "danger" | "warning" | "default" => {
    switch (status) {
      case "DONE":
        return "success";
      case "FAILED":
        return "danger";
      case "PENDING":
        return "warning";
      case "CANCELED":
        return "default";
      default:
        return "default";
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    loadCards();
    requestHistory();
  }, [page, size]);

  // billingInfo가 null인 경우 Empty 상태
  if (!billingInfo) {
    return (
      <div className="container mx-auto p-2">
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <div className="w-24 h-24 rounded-full bg-default-100 flex items-center justify-center">
            <Receipt className="h-8 w-8 text-default-400" />
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              결제할 내용이 없습니다
            </h2>
            <p className="text-default-500">
              현재 등록된 구독 플랜이 없습니다.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2 space-y-6">
      {/* 제목 */}
      <div className="flex flex-col space-y-4">
        <h1 className="text-2xl font-bold">기업 결제 관리</h1>
      </div>

      {/* 결제 정보 섹션 */}
      <Card shadow="sm">
        <CardHeader className="pb-3">
          <div className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5" />
            <h2 className="text-lg font-semibold">구독 플랜 정보</h2>
          </div>
        </CardHeader>
        <Divider />
        <CardBody className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-default-50 rounded-lg">
              <div className="text-sm text-default-500 mb-1">월간 요금</div>
              <div className="text-2xl font-bold text-foreground">
                {formatKRW(billingInfo.monthlyPrice)}
              </div>
            </div>
            <div className="p-4 bg-default-50 rounded-lg">
              <div className="text-sm text-default-500 mb-1">연간 요금</div>
              <div className="text-2xl font-bold text-foreground">
                {formatKRW(billingInfo.yearlyPrice)}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* 결제 시작 섹션 */}
      <Card shadow="sm">
        <CardHeader className="pb-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <h2 className="text-lg font-semibold">결제</h2>
          </div>
        </CardHeader>
        <Divider />
        <CardBody className="p-4 space-y-4">
          <RadioGroup
            label="결제 주기 선택"
            value={selectedBillingCycle}
            onValueChange={(value) =>
              setSelectedBillingCycle(value as "MONTHLY" | "YEARLY")
            }
            orientation="horizontal"
          >
            <Radio value="MONTHLY">
              월간 결제 ({formatKRW(billingInfo.monthlyPrice)})
            </Radio>
            <Radio value="YEARLY">
              연간 결제 ({formatKRW(billingInfo.yearlyPrice)})
            </Radio>
          </RadioGroup>
          <Button
            color="primary"
            size="lg"
            onPress={() => setIsConfirmPaymentModalOpen(true)}
            isDisabled={!billingInfo.isPaymentEnabled || cards.length === 0}
          >
            결제하기
          </Button>
          {!billingInfo.isPaymentEnabled ? (
            <p className="text-sm text-warning">현재 결제할 금액이 없습니다.</p>
          ) : cards.length === 0 ? (
            <p className="text-sm text-warning">
              결제를 시작하려면 먼저 카드를 등록해주세요.
            </p>
          ) : null}
        </CardBody>
      </Card>

      {/* 카드 관리 섹션 */}
      <Card shadow="sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <h2 className="text-lg font-semibold">등록된 카드</h2>
          </div>
          <Button
            color="primary"
            startContent={<Plus className="h-4 w-4" />}
            onPress={handleAddCard}
            size="sm"
          >
            카드 등록
          </Button>
        </CardHeader>
        <Divider />
        <CardBody className="p-4">
          {isLoadingCards ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" color="primary" />
            </div>
          ) : cards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="w-16 h-16 rounded-full bg-default-100 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-default-400" />
              </div>
              <p className="text-default-500">등록된 카드가 없습니다</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className={`group relative overflow-hidden rounded-lg p-3 transition-all duration-300 hover:shadow-md ${
                    card.isPrimary
                      ? "bg-gradient-to-br from-primary-500/20 via-primary-400/10 to-primary-500/20 border-2 border-primary-500/40"
                      : "bg-gradient-to-br from-default-100 to-default-50 border border-default-200/50"
                  }`}
                >
                  {/* 대표 카드 배지 */}
                  {card.isPrimary && (
                    <div className="absolute top-2 right-2">
                      <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-primary-500/90 rounded-full">
                        <Star className="h-2.5 w-2.5 text-white fill-white" />
                        <span className="text-[10px] font-medium text-white">
                          대표
                        </span>
                      </div>
                    </div>
                  )}

                  {/* 카드 아이콘 */}
                  <div className="mb-2.5">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        card.isPrimary
                          ? "bg-primary-500/20"
                          : "bg-default-200/50"
                      }`}
                    >
                      <CreditCard
                        className={`h-4.5 w-4.5 ${
                          card.isPrimary
                            ? "text-primary-600"
                            : "text-default-600"
                        }`}
                      />
                    </div>
                  </div>

                  {/* 카드 정보 */}
                  <div className="space-y-1 mb-2.5">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {card.cardCompany}
                    </h3>
                    <p className="text-xs font-mono text-default-600 truncate">
                      {card.cardNumber}
                    </p>
                    <p className="text-[10px] text-default-500">
                      {card.cardType} · {card.ownerType}
                    </p>
                  </div>

                  {/* 액션 버튼 */}
                  <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {!card.isPrimary && (
                      <Button
                        size="sm"
                        variant="flat"
                        className="flex-1 h-7 text-xs"
                        onPress={() => handleSetPrimaryCard(card.id)}
                      >
                        대표 설정
                      </Button>
                    )}
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      isIconOnly
                      className="h-7 w-7 min-w-7"
                      onPress={() => setCardToDelete(card)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* 결제 이력 섹션 */}
      <Card shadow="sm">
        <CardHeader className="pb-3">
          <div className="flex items-center space-x-2">
            <Receipt className="h-5 w-5" />
            <h2 className="text-lg font-semibold">결제 이력</h2>
          </div>
        </CardHeader>
        <Divider />
        <CardBody className="p-0">
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" color="primary" />
            </div>
          ) : (
            <Table
              aria-label="결제 이력 테이블"
              classNames={{
                td: "border-b border-divider/50",
              }}
              shadow="none"
            >
              <TableHeader>
                <TableColumn>결제명</TableColumn>
                <TableColumn className="hidden md:table-cell">
                  승인일시
                </TableColumn>
                <TableColumn>금액</TableColumn>
                <TableColumn className="hidden sm:table-cell">
                  카드번호
                </TableColumn>
                <TableColumn>상태</TableColumn>
                <TableColumn className="hidden lg:table-cell">타입</TableColumn>
              </TableHeader>
              <TableBody
                emptyContent={
                  <div className="flex flex-col items-center justify-center py-8 gap-4">
                    <div className="w-16 h-16 rounded-full bg-default-100 flex items-center justify-center">
                      <Receipt className="h-8 w-8 text-default-400" />
                    </div>
                    <p className="text-default-500">결제 이력이 없습니다</p>
                  </div>
                }
              >
                {histories.map((history, index) => (
                  <TableRow key={`${history.transactionId}-${index}`}>
                    <TableCell>
                      <div className="font-medium">
                        {history.paymentName || "-"}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm">
                        {dayjs(history.approvedAt).format("YYYY.MM.DD HH:mm")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatKRW(history.paymentPrice)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="text-sm text-default-500">
                        {history.cardNumber}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="sm"
                        color={getPaymentStatusColor(history.status)}
                        variant="flat"
                      >
                        {history.status}
                      </Chip>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Chip size="sm" variant="flat">
                        {history.type}
                      </Chip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            total={totalPages}
            page={page}
            onChange={(newPage) =>
              navigate({
                search: (prev) => ({ ...prev, page: newPage }),
              })
            }
            showControls
            showShadow
            color="primary"
          />
        </div>
      )}

      {/* 결제 확인 모달 */}
      <Modal
        isOpen={isConfirmPaymentModalOpen}
        onOpenChange={setIsConfirmPaymentModalOpen}
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        backdrop="blur"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-warning" />
              <span>결제 확인</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p className="text-sm">다음 내용으로 결제를 진행하시겠습니까?</p>
              <div className="p-4 bg-default-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-default-600">결제 주기:</span>
                  <span className="text-sm font-medium">
                    {selectedBillingCycle === "MONTHLY" ? "월간" : "연간"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-default-600">결제 금액:</span>
                  <span className="text-sm font-medium">
                    {formatKRW(
                      selectedBillingCycle === "MONTHLY"
                        ? billingInfo.monthlyPrice
                        : billingInfo.yearlyPrice
                    )}
                  </span>
                </div>
              </div>
              <p className="text-xs text-default-500">
                등록된 대표 카드로 결제가 진행됩니다.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="flat"
              onPress={() => setIsConfirmPaymentModalOpen(false)}
              isDisabled={isStartingPayment}
            >
              취소
            </Button>
            <Button
              color="primary"
              onPress={handleStartPayment}
              isLoading={isStartingPayment}
            >
              {isStartingPayment ? "처리 중..." : "결제 시작"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 카드 삭제 확인 모달 */}
      <Modal
        isOpen={!!cardToDelete}
        onOpenChange={(isOpen) => !isOpen && setCardToDelete(null)}
        backdrop="blur"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-danger" />
              <span>카드 삭제 확인</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p className="text-sm">정말로 이 카드를 삭제하시겠습니까?</p>
              {cardToDelete && (
                <div className="p-4 bg-default-50 rounded-lg">
                  <div className="font-medium">{cardToDelete.cardCompany}</div>
                  <div className="text-sm text-default-500">
                    {cardToDelete.cardNumber}
                  </div>
                </div>
              )}
              <p className="text-xs text-warning">
                이 작업은 취소할 수 없습니다.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="flat"
              onPress={() => setCardToDelete(null)}
              isDisabled={isDeletingCard}
            >
              취소
            </Button>
            <Button
              color="danger"
              onPress={handleDeleteCard}
              isLoading={isDeletingCard}
            >
              {isDeletingCard ? "삭제 중..." : "삭제"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
